import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  GlobeAltIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';

const ApiIntegrationSettings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' or 'automation'
  const [formData, setFormData] = useState({
    loginUrl: '',
    method: 'POST',
    email: '',
    password: '',
    requestFormat: 'json',
    isActive: true,
    additionalConfig: {
      timeout: 30000,
      retryAttempts: 3,
      headers: {}
    }
  });

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [customHeaders, setCustomHeaders] = useState([]);
  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderValue, setNewHeaderValue] = useState('');
  
  // Automation settings state
  const [automations, setAutomations] = useState([]);
  const [automationLoading, setAutomationLoading] = useState(true);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [automationFormData, setAutomationFormData] = useState({
    name: '',
    email: '',
    password: '',
    isActive: true
  });
  const [showAutomationPassword, setShowAutomationPassword] = useState({});

  useEffect(() => {
    if (activeTab === 'settings') {
      loadSettings();
    } else if (activeTab === 'automation') {
      loadAutomations();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api-integration/settings');
      const data = response.data.data;
      
      setSettings(data);
      setFormData({
        loginUrl: data.apiConfig?.loginUrl || '',
        method: data.apiConfig?.method || 'POST',
        email: data.apiConfig?.email || '',
        password: data.apiConfig?.password || '', // Load password for editing
        requestFormat: data.apiConfig?.requestFormat || 'json',
        isActive: data.isActive !== undefined ? data.isActive : true,
        additionalConfig: {
          timeout: data.additionalConfig?.timeout || 30000,
          retryAttempts: data.additionalConfig?.retryAttempts || 3,
          headers: data.additionalConfig?.headers || {}
        }
      });

      // Convert headers object to array for easier management
      const headersArray = Object.entries(data.additionalConfig?.headers || {}).map(([key, value]) => ({ key, value }));
      setCustomHeaders(headersArray);
    } catch (error) {
      console.error('Failed to load API integration settings:', error);
      toast.error('Failed to load API integration settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      additionalConfig: {
        ...prev.additionalConfig,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Client-side validation
      if (!formData.loginUrl.trim()) {
        toast.error('Login URL is required');
        return;
      }
      
      if (!formData.email.trim()) {
        toast.error('Email is required');
        return;
      }
      
      // Convert headers array back to object
      const headersObject = {};
      customHeaders.forEach(header => {
        if (header.key && header.value) {
          headersObject[header.key] = header.value;
        }
      });

      const updateData = {
        loginUrl: formData.loginUrl.trim(),
        method: formData.method,
        email: formData.email.trim(),
        requestFormat: formData.requestFormat,
        isActive: formData.isActive,
        additionalConfig: {
          ...formData.additionalConfig,
          headers: headersObject
        }
      };

      // Only include password if it's been changed
      if (formData.password !== undefined) {
        updateData.password = formData.password;
      }

      await api.put('/api-integration/settings', updateData);
      toast.success('API integration settings saved successfully');
      loadSettings(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to save API integration settings:', error);
      toast.error('Failed to save API integration settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      
      const response = await api.post('/api-integration/test');
      
      if (response.data.success) {
        toast.success('API connection test successful!');
        // Reload settings to get updated test results
        await loadSettings();
      } else {
        toast.error('API connection test failed');
      }
    } catch (error) {
      console.error('API connection test failed:', error);
      toast.error(error.response?.data?.message || 'API connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const loadAutomations = async () => {
    try {
      setAutomationLoading(true);
      const response = await api.get('/api-integration/automation');
      setAutomations(response.data.data?.automations || []);
    } catch (error) {
      console.error('Failed to load automation settings:', error);
      toast.error('Failed to load automation settings');
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleAddAutomation = () => {
    setEditingAutomation('new');
    setAutomationFormData({
      name: '',
      email: '',
      password: '',
      isActive: true
    });
  };

  const handleEditAutomation = (automation) => {
    setEditingAutomation(automation._id);
    setAutomationFormData({
      name: automation.name,
      email: automation.email,
      password: automation.password,
      isActive: automation.isActive
    });
  };

  const handleSaveAutomation = async () => {
    try {
      if (!automationFormData.name.trim() || !automationFormData.email.trim() || !automationFormData.password.trim()) {
        toast.error('Name, email, and password are required');
        return;
      }

      setAutomationSaving(true);

      if (editingAutomation && editingAutomation !== 'new') {
        await api.put(`/api-integration/automation/${editingAutomation}`, automationFormData);
        toast.success('Automation updated successfully');
      } else {
        await api.post('/api-integration/automation', automationFormData);
        toast.success('Automation added successfully');
      }

      setEditingAutomation(null);
      setAutomationFormData({
        name: '',
        email: '',
        password: '',
        isActive: true
      });
      setShowAutomationPassword({});
      await loadAutomations();
    } catch (error) {
      console.error('Failed to save automation:', error);
      toast.error(error.response?.data?.message || 'Failed to save automation');
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleDeleteAutomation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this automation?')) {
      return;
    }

    try {
      await api.delete(`/api-integration/automation/${id}`);
      toast.success('Automation deleted successfully');
      await loadAutomations();
    } catch (error) {
      console.error('Failed to delete automation:', error);
      toast.error('Failed to delete automation');
    }
  };

  const togglePasswordVisibility = (id) => {
    setShowAutomationPassword(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  if (loading && activeTab === 'settings') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <GlobeAltIcon className="h-8 w-8 text-blue-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">API Integration Settings</h1>
            <p className="text-gray-600">
              {user?.role === 'admin' ? 'Global API integration settings' : 'Your personal API integration settings'}
            </p>
          </div>
        </div>

        {/* Settings Type Indicator */}
        <div className="flex items-center mb-6">
          {user?.role === 'admin' ? (
            <div className="flex items-center text-blue-600">
              <GlobeAltIcon className="h-5 w-5 mr-2" />
              <span className="font-medium">Global Settings</span>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <UserIcon className="h-5 w-5 mr-2" />
              <span className="font-medium">Personal Settings</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab('automation')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'automation'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Automation
          </button>
        </nav>
      </div>

      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
      <div className="bg-white shadow rounded-lg">
        {/* API Configuration Section */}
        <div className="border-b border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">API Configuration</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Login URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Login URL *
              </label>
              <input
                type="url"
                value={formData.loginUrl}
                onChange={(e) => handleInputChange('loginUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://api.example.com/login"
                required
              />
            </div>

            {/* HTTP Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HTTP Method *
              </label>
              <select
                value={formData.method}
                onChange={(e) => handleInputChange('method', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            {/* Request Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Request Format
              </label>
              <select
                value={formData.requestFormat}
                onChange={(e) => handleInputChange('requestFormat', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="json">JSON</option>
                <option value="form-data">Form Data</option>
                <option value="x-www-form-urlencoded">URL Encoded</option>
              </select>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="api@example.com"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter API password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Password is stored in plain text for API authentication
              </p>
            </div>

            {/* Active Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Configuration Section */}
        <div className="border-b border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Additional Configuration</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeout (milliseconds)
              </label>
              <input
                type="number"
                value={formData.additionalConfig.timeout}
                onChange={(e) => handleAdditionalConfigChange('timeout', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1000"
                max="300000"
              />
            </div>

            {/* Retry Attempts */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Retry Attempts
              </label>
              <input
                type="number"
                value={formData.additionalConfig.retryAttempts}
                onChange={(e) => handleAdditionalConfigChange('retryAttempts', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="10"
              />
            </div>

            {/* Custom Headers Section */}
            <div className="mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">Custom Headers</h3>
              <div className="space-y-3">
                {customHeaders.map((header, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={header.key}
                      onChange={(e) => {
                        const newHeaders = [...customHeaders];
                        newHeaders[index].key = e.target.value;
                        setCustomHeaders(newHeaders);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Header name"
                    />
                    <input
                      type="text"
                      value={header.value}
                      onChange={(e) => {
                        const newHeaders = [...customHeaders];
                        newHeaders[index].value = e.target.value;
                        setCustomHeaders(newHeaders);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Header value"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newHeaders = customHeaders.filter((_, i) => i !== index);
                        setCustomHeaders(newHeaders);
                      }}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newHeaderKey}
                    onChange={(e) => setNewHeaderKey(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Header name"
                  />
                  <input
                    type="text"
                    value={newHeaderValue}
                    onChange={(e) => setNewHeaderValue(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Header value"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newHeaderKey && newHeaderValue) {
                        setCustomHeaders([...customHeaders, { key: newHeaderKey, value: newHeaderValue }]);
                        setNewHeaderKey('');
                        setNewHeaderValue('');
                      }
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Last Test Results Section */}
        {settings?.lastTestResult && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Last Test Results</h2>
            
            <div className="flex items-center p-4 rounded-lg border">
              {settings.lastTestResult.success ? (
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
              ) : (
                <XCircleIcon className="h-6 w-6 text-red-500 mr-3" />
              )}
              
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${
                    settings.lastTestResult.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {settings.lastTestResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {settings.lastTestResult.testedAt ? 
                      new Date(settings.lastTestResult.testedAt).toLocaleString() : 
                      'Unknown'
                    }
                  </span>
                </div>
                {settings.lastTestResult.message && (
                  <p className="text-sm text-gray-600 mt-1">
                    {settings.lastTestResult.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-6 bg-gray-50 rounded-b-lg">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            
            <button
              onClick={handleTestConnection}
              disabled={testing || !formData.loginUrl || !formData.email}
              className="flex-1 sm:flex-none px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Automation Tab Content */}
      {activeTab === 'automation' && (
        <div className="bg-white shadow rounded-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">Automation Credentials</h2>
              <button
                onClick={handleAddAutomation}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Add Automation
              </button>
            </div>

            {automationLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* Add/Edit Form */}
                {editingAutomation !== null && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-md font-medium text-gray-900 mb-4">
                      {editingAutomation !== 'new' ? 'Edit Automation' : 'Add New Automation'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={automationFormData.name}
                          onChange={(e) => setAutomationFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., TM-O Automation"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={automationFormData.email}
                          onChange={(e) => setAutomationFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="automation@example.com"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password *
                        </label>
                        <div className="relative">
                          <input
                            type={showAutomationPassword[editingAutomation] ? 'text' : 'password'}
                            value={automationFormData.password}
                            onChange={(e) => setAutomationFormData(prev => ({ ...prev, password: e.target.value }))}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter password"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility(editingAutomation || 'new')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showAutomationPassword[editingAutomation || 'new'] ? (
                              <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <div className="flex items-center mt-2">
                          <input
                            type="checkbox"
                            checked={automationFormData.isActive}
                            onChange={(e) => setAutomationFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 text-sm text-gray-700">
                            Active
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={handleSaveAutomation}
                        disabled={automationSaving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {automationSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingAutomation(null);
                          setAutomationFormData({
                            name: '',
                            email: '',
                            password: '',
                            isActive: true
                          });
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Automation List */}
                {automations.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No automation credentials configured yet.</p>
                    <button
                      onClick={handleAddAutomation}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add Your First Automation
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {automations.map((automation) => (
                      <div
                        key={automation._id}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="text-md font-medium text-gray-900">{automation.name}</h3>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                automation.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {automation.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{automation.email}</p>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-gray-500">Password:</span>
                              <span className="text-xs font-mono text-gray-700">
                                {showAutomationPassword[automation._id]
                                  ? automation.password
                                  : '••••••••'}
                              </span>
                              <button
                                type="button"
                                onClick={() => togglePasswordVisibility(automation._id)}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                {showAutomationPassword[automation._id] ? 'Hide' : 'Show'}
                              </button>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditAutomation(automation)}
                              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteAutomation(automation._id)}
                              className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Information Box - Only show for Settings tab */}
      {activeTab === 'settings' && (
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Important Notes</h3>
            <div className="mt-1 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Passwords are stored in plain text for direct API authentication</li>
                <li>Test your connection before saving to ensure it works</li>
                <li>{user?.role === 'admin' ? 
                  'These settings will apply globally to all users' : 
                  'These settings are personal to your account'
                }</li>
                <li>Connection timeouts and retry attempts help with reliability</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ApiIntegrationSettings; 